import { memo } from "react";
import { defaultAnimateLayoutChanges, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicator from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  STOP_STATUS_COLORS,
  STOP_STATUS_LABELS,
  type HouseVisitStopRecord,
  type HouseVisitStopStatus,
} from "../types";

type Props = {
  stop: HouseVisitStopRecord;
  busy: boolean;
  canRemove?: boolean;
  onRemove?: (id: string) => void;
  /** Tap the main content (not the drag handle) to open the stop. */
  onOpen?: (id: string) => void;
};

function SortableStopRowInner({ stop, busy, onRemove, canRemove = false, onOpen }: Props) {
  const status = (stop.status || "PENDING") as HouseVisitStopStatus;
  const address =
    typeof stop.address === "string" && stop.address.trim() ? stop.address.trim() : null;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(stop.id),
    disabled: busy,
    animateLayoutChanges: (args) => (args.isSorting ? defaultAnimateLayoutChanges(args) : false),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 1.25,
        border: 1,
        borderColor: isDragging ? "primary.main" : "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: isDragging ? 4 : 0,
        opacity: isDragging ? 0.92 : 1,
        minHeight: 72,
        transition: (t) =>
          t.transitions.create(["border-color", "box-shadow", "opacity"], {
            duration: t.transitions.duration.shorter,
          }),
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        sx={{
          cursor: busy ? "default" : "grab",
          touchAction: "none",
          color: "action.active",
          display: "flex",
          p: 0.75,
          borderRadius: 1,
          "&:hover": busy ? undefined : { bgcolor: (t) => alpha(t.palette.text.primary, 0.06) },
        }}
        title={busy ? undefined : "Drag to reorder"}
      >
        <DragIndicator />
      </Box>

      <Box
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          typography: "caption",
          fontWeight: 700,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
          color: "primary.main",
        }}
      >
        {stop.sortOrder + 1}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: onOpen ? "pointer" : "default",
          borderRadius: 1,
          py: 0.25,
          "&:hover": onOpen ? { bgcolor: (t) => alpha(t.palette.text.primary, 0.04) } : undefined,
        }}
        onClick={() => {
          if (onOpen && !isDragging) onOpen(String(stop.id));
        }}
        role={onOpen ? "button" : undefined}
      >
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {stop.hofName || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          ITS {stop.hofItsNo}
          {address ? ` · ${address}` : ""}
        </Typography>
      </Box>

      <Chip
        size="small"
        label={STOP_STATUS_LABELS[status] ?? status}
        color={STOP_STATUS_COLORS[status] ?? "default"}
        variant="outlined"
      />

      {canRemove && onRemove ? (
        <IconButton
          size="large"
          aria-label="Remove stop"
          disabled={busy}
          onClick={() => onRemove(String(stop.id))}
          sx={{ touchAction: "manipulation", color: "text.secondary" }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      ) : null}
    </Box>
  );
}

export default memo(SortableStopRowInner);
